import type { TaskState } from '../../api/rmf-client';
import type { TaskPhase, TaskRequest } from '../../api/v1';
import { phaseLabel } from './task-phase-label';
import { plannedPlaces } from './task-planned-places';

/**
 * The Tasks table's Location column: one short answer to "where is this
 * job", for any task.
 *
 * TWO SOURCES, IN THIS ORDER, never blended:
 *
 *  1. `task.phases` -- where RMF says the job goes or went. The same
 *     record and the same `phaseLabel` the drawer and the CSV export
 *     read, so the three cannot name different places for one job.
 *  2. the dispatch request -- where it was ASKED to go. A queued task
 *     publishes no phases at all (see `TaskPlannedList`), so without
 *     this every queued row would read as a dash.
 *
 * Only phases that NAME a place count. `phaseLabel` falls back to the
 * phase's description, which for a clean is "Sequence" and for RMF's own
 * charge is "Charge Battery" -- right in a list of steps, wrong in a
 * column headed Location.
 */
export interface TaskLocation {
  /** What the cell shows. */
  text: string;
  /** Every place, in order -- the hover title when `text` shortens it. */
  full: string;
}

type Shape = 'single' | 'route' | 'list';

function summarise(places: string[], shape: Shape): TaskLocation | null {
  const first = places[0];
  if (first === undefined) return null;
  const full = places.join(' → ');
  if (shape === 'single') return { text: first, full: first };
  if (shape === 'route') {
    // Pickup → dropoff. Only the first two: a delivery is defined by
    // them, and the CSV's pickup/dropoff read the same two positions.
    return { text: places.slice(0, 2).join(' → '), full };
  }
  if (places.length === 1) return { text: first, full };
  // A patrol of nine places will not fit a column. Where it starts and
  // how many more it has is enough to recognise it; the title has all.
  return { text: `${first} + ${places.length - 1} more`, full };
}

/** The shape a category's places take. Mirrors `waypointColumns` in
 *  `TasksScreen.tsx` (one destination; pickup then dropoff; an ordered
 *  list for everything else), plus the one-place categories that
 *  function predates. */
function shapeOf(category: string | undefined): Shape {
  switch (category) {
    case 'station_docking':
    case 'clean':
    case 'dustbag':
    case 'charge':
      return 'single';
    case 'delivery':
      return 'route';
    default:
      return 'list';
  }
}

function namesPlace(phase: TaskPhase): boolean {
  const place = phase.params?.place;
  return typeof place === 'string' && place.trim() !== '';
}

/**
 * From a list of place names already pulled out of a task -- the durable
 * record's `visited_places`, which the older view reads instead of phases
 * -- or null when it is empty. Same shapes as the live column, so a task
 * reads the same on either list.
 */
export function locationFromPlaces(
  category: string | undefined,
  places: ReadonlyArray<string>,
): TaskLocation | null {
  return summarise(
    places.filter((p) => p.trim() !== ''),
    shapeOf(category),
  );
}

/** From the task's own phases, or null when none names a place. */
export function locationFromPhases(task: TaskState): TaskLocation | null {
  const places = (task.phases ?? []).filter(namesPlace).map(phaseLabel);
  return summarise(places, shapeOf(task.type));
}

/**
 * From a dispatch request, or null when it names no place.
 *
 * `category` should be the STORED request's own category where there is
 * one -- `TaskPlannedList` explains why the live projection's can read
 * `custom` for a clean.
 */
export function locationFromRequest(
  category: string | undefined,
  request: TaskRequest | null | undefined,
): TaskLocation | null {
  const planned = plannedPlaces(category, request);
  if (planned.length === 0) return null;
  const roles = new Set(planned.map((p) => p.role));
  const shape: Shape =
    roles.has('pickup') || roles.has('dropoff')
      ? 'route'
      : roles.has('waypoint')
        ? 'list'
        : 'single';
  return summarise(
    planned.map((p) => p.place),
    shape,
  );
}
